import { PROGRESS } from "@/constants/testIds";
import { useEffect, useState } from "react";

const MILESTONES = [
  "HUB 01 — DOWNTOWN DISPATCH SECURED",
  "FLEET 01 — 3 ELECTRIC VANS RESERVED",
  "ROUTE OPS SOFTWARE V0 IN TESTING",
  "FIRST 12 LOCAL SHOP PARTNERS SIGNED",
  "DRIVER PARTNER RECRUITMENT OPEN",
];

export default function ProgressSection({ stats }) {
  const pct = stats?.percent ?? 0;
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(pct), 150);
    return () => clearTimeout(t);
  }, [pct]);

  return (
    <section id="progress" className="relative py-16 sm:py-24 border-y border-white/10 bg-slate-950/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500">// Live Telemetry</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight mt-2">
              The road so far
            </h2>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-400">
            Updated in real time
          </div>
        </div>

        <div className="industrial-border bg-slate-950 p-6 sm:p-8">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Amount Raised</div>
              <div data-testid={PROGRESS.raised} className="font-display text-4xl sm:text-5xl font-black text-white">
                ${(stats?.raised ?? 0).toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Goal</div>
              <div data-testid={PROGRESS.goal} className="font-display text-xl sm:text-2xl font-bold text-slate-300">
                ${Number(stats?.goal ?? 250000).toLocaleString()}
              </div>
            </div>
          </div>
          <div data-testid={PROGRESS.bar} className="relative h-5 bg-slate-900 border border-white/10 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-kinetic transition-all duration-[1500ms] ease-out"
              style={{ width: `${width}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 opacity-30"
              style={{
                width: `${width}%`,
                backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.3) 0 6px, transparent 6px 12px)",
              }}
            />
          </div>
          <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <span>{pct.toFixed(1)}% complete</span>
            <span>Phase 01</span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <StatCard testid={PROGRESS.backers} label="Backers" value={stats?.backers ?? 0} />
          <StatCard label="Donated" value={`$${(stats?.donated ?? 0).toLocaleString()}`} />
          <StatCard testid={PROGRESS.daysLive} label="Days Live" value={stats?.days_live ?? 0} />
          <StatCard testid={PROGRESS.daysLeft} label="Days Left" value={stats?.days_left ?? 0} accent />
        </div>

        <div className="mt-10 overflow-hidden border border-white/10 bg-slate-950">
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-900/80 border-b border-white/10">
            <span className="w-2 h-2 bg-kinetic rounded-full pulse-dot" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Dispatch Ticker</span>
          </div>
          <div className="ticker-tape overflow-hidden">
            <div className="flex gap-12 py-3 whitespace-nowrap animate-marquee font-mono text-xs text-slate-300">
              {[...MILESTONES, ...MILESTONES].map((m, i) => (
                <span key={i} className="flex items-center gap-3">
                  <span className="text-kinetic">◆</span> {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className={`p-5 border ${accent ? "border-kinetic/60 bg-[rgba(255,69,0,0.05)]" : "border-white/10 bg-slate-950"}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div data-testid={testid} className={`font-display text-3xl font-bold mt-1 ${accent ? "text-kinetic" : "text-white"}`}>{value}</div>
    </div>
  );
}
